import { Component, inject, OnInit, ViewContainerRef } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ToastService, ToastType } from 'vis-ods-angular';
import { TranslocoUtilityService } from './services/transloco-utility/transloco-utility.service';
import { UserDataService } from './services/user-data/user-data.service';
import { MockSwitchComponent } from '../testing/mock-switch/mock-switch.component';
import { MockValuesComponent } from '../testing/mock-values/mock-values.component';
import { DateTimeFormatPipe } from './shared/pipes/dateTime-format.pipe';
import { ConfigurationStore } from './state-management/stores/configuration.store';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	imports: [RouterOutlet, MockSwitchComponent, MockValuesComponent],
	providers: [DateTimeFormatPipe],
})
export class AppComponent implements OnInit {
	private readonly utils = inject(TranslocoUtilityService);
	private readonly toastService = inject(ToastService);
	private readonly translocoService = inject(TranslocoService);
	private readonly userDataService = inject(UserDataService);
	private readonly dateTimeFormatPipe = inject(DateTimeFormatPipe);
	private readonly configurationStore = inject(ConfigurationStore);
	private readonly blockRemoveDate =
		this.configurationStore.settings()?.block_submit_order_remove_date ?? '';
	private readonly isUserBlockedFromOrdering = this.configurationStore.isBlockedFromOrdering;

	private readonly userLanguage = this.userDataService.getUserLanguage();

	/**
	 * Dont delete the viewcontainerRef its being used somewere...i tried 🥲
	 */
	constructor(public viewContainerRef: ViewContainerRef) {}

	async ngOnInit() {
		// Needs to be called first, to be fast enough to observe the transloco events
		this.utils.translationFallbackLoaded().then(async (result) => {
			if (
				result &&
				!(await this.userDataService.isKeyInStorage(
					'languageNotAvailableToastDisplayed',
					this.userLanguage,
				))
			) {
				this.displayFallbackLanguageToast();
			}
		});

		this.utils.translationsLoaded().then(async () => {
			// Checks if user is allowed to order and display a toast once if he is blocked from ordering.
			if (
				this.isUserBlockedFromOrdering() &&
				!(await this.userDataService.isKeyInStorage('blockedOrderToast', this.userLanguage))
			) {
				this.displayBlockedFromOrderingToast();
			}
		});
	}

	private displayBlockedFromOrderingToast() {
		this.toastService.addToast({
			automaticDeleteTime: 3000,
			variant: ToastType.info,
			headline: this.translocoService.translate(
				'generalComponents.blockedFromOrderingToast.headlines.title',
			),
			description: this.translocoService.translate(
				'generalComponents.blockedFromOrderingToast.texts.message',
				{ date: this.dateTimeFormatPipe.transform(this.blockRemoveDate) },
			),
		});

		if (this.userLanguage) {
			this.userDataService.setPropertyInStorage('blockedOrderToast', this.userLanguage);
		}
	}

	private displayFallbackLanguageToast() {
		this.toastService.addToast({
			automaticDeleteTime: 3000,
			variant: ToastType.info,
			headline: this.translocoService.translate(
				'generalComponents.fallbackLanguageToast.headlines.title',
				{ language: this.userLanguage },
			),
			description: this.translocoService.translate(
				'generalComponents.fallbackLanguageToast.texts.message',
			),
		});

		if (this.userLanguage) {
			this.userDataService.setPropertyInStorage(
				'languageNotAvailableToastDisplayed',
				this.userLanguage,
			);
		}
	}
}
